/* iter100 — Birthday SMS template configuration tab.
 * Admins can edit the SMS body sent by the daily birthday cron
 * plus toggle it on/off.  Preview shows the message with sample data.
 */
import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Input } from '../ui/input';
import { Cake, FloppyDisk } from '@phosphor-icons/react';
import { toast } from 'sonner';
import api from '../../lib/api';

const DEFAULT_TPL =
  "Joyeux anniversaire à {first_name} ! Toute l'equipe de "
  + "{school_name} lui souhaite une belle journee. {signature}";

const VARS = ['{first_name}', '{last_name}', '{school_name}', '{signature}'];

const BirthdaySmsTab = () => {
  const [tpl, setTpl] = useState('');
  const [signature, setSignature] = useState('');
  const [enabled, setEnabled] = useState(true);
  const [schoolName, setSchoolName] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      const [s, c] = await Promise.all([
        api.get('/dashboard/birthdays/settings'),
        api.get('/school/config'),
      ]);
      setTpl(s.data.birthday_sms_template || DEFAULT_TPL);
      setSignature(s.data.birthday_sms_signature || '');
      setEnabled(!!s.data.birthday_sms_enabled);
      setSchoolName(c.data?.name || 'Mon École');
    } catch (e) {
      toast.error(e?.response?.data?.detail
        || 'Erreur chargement paramètres SMS');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const insertVar = (v) =>
    setTpl(prev => (prev || '') + v);

  const preview = (() => {
    try {
      return (tpl || '')
        .replaceAll('{first_name}', 'Amina')
        .replaceAll('{last_name}', 'Diallo')
        .replaceAll('{school_name}', schoolName)
        .replaceAll('{signature}', signature);
    } catch { return tpl; }
  })();

  const handleSave = async () => {
    if (!tpl.trim()) {
      toast.error('Le modèle ne peut être vide');
      return;
    }
    setSaving(true);
    try {
      await api.put('/dashboard/birthdays/settings', {
        birthday_sms_template: tpl,
        birthday_sms_signature: signature,
        birthday_sms_enabled: enabled,
      });
      toast.success('Paramètres SMS enregistrés');
    } catch (e) {
      toast.error(e?.response?.data?.detail
        || 'Erreur d\'enregistrement');
    } finally { setSaving(false); }
  };

  const handleReset = () => setTpl(DEFAULT_TPL);

  if (loading) {
    return (
      <Card className="border border-[#E2E8F0] shadow-none">
        <CardContent className="p-6 text-[#94A3B8]">Chargement…</CardContent>
      </Card>
    );
  }

  return (
    <Card className="border border-[#E2E8F0] shadow-none"
      data-testid="birthday-sms-card">
      <CardHeader>
        <CardTitle className="font-outfit flex items-center gap-2">
          <Cake size={20} className="text-pink-600" weight="duotone" />
          SMS d&apos;anniversaire automatique
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center gap-3 p-3 bg-pink-50 border border-pink-200 rounded-md">
          <Switch
            checked={enabled}
            onCheckedChange={setEnabled}
            data-testid="bday-sms-toggle" />
          <div>
            <div className="font-semibold text-sm text-[#0F172A]">
              Envoi quotidien activé
            </div>
            <div className="text-xs text-[#64748B]">
              Un SMS est envoyé chaque matin (07:00 UTC) aux parents des élèves
              dont c&apos;est l&apos;anniversaire.
            </div>
          </div>
        </div>

        <div className="space-y-2">
          <Label>Modèle du message (max 320 caractères)</Label>
          <textarea
            value={tpl}
            onChange={e => setTpl(e.target.value.slice(0, 320))}
            rows={5}
            data-testid="bday-sms-template"
            className="w-full rounded-md border border-[#E2E8F0] p-2 text-sm focus:outline-none focus:ring-2 focus:ring-pink-400"
            placeholder={DEFAULT_TPL} />
          <div className="flex items-center justify-between text-[11px] text-[#94A3B8]">
            <div className="flex flex-wrap gap-1">
              {VARS.map(v => (
                <button
                  key={v}
                  type="button"
                  onClick={() => insertVar(v)}
                  data-testid={`bday-var-${v.slice(1, -1)}`}
                  className="bg-slate-100 hover:bg-slate-200 rounded px-1.5 py-0.5 font-mono text-slate-700">
                  {v}
                </button>
              ))}
            </div>
            <span>{(tpl || '').length}/320</span>
          </div>
        </div>

        <div className="space-y-2">
          <Label>Signature (optionnelle)</Label>
          <Input
            value={signature}
            onChange={e => setSignature(e.target.value)}
            placeholder="- La Direction"
            data-testid="bday-sms-signature" />
        </div>

        <div className="space-y-1">
          <Label>Aperçu</Label>
          <div className="p-3 rounded-md bg-slate-50 border border-slate-200 text-sm text-[#0F172A] whitespace-pre-wrap"
            data-testid="bday-sms-preview">
            {preview}
          </div>
        </div>

        <div className="flex items-center gap-2 pt-2">
          <Button
            onClick={handleSave}
            disabled={saving}
            data-testid="bday-sms-save"
            className="bg-pink-600 hover:bg-pink-700 text-white">
            <FloppyDisk size={16} className="mr-2" />
            {saving ? 'Enregistrement…' : 'Enregistrer'}
          </Button>
          <Button
            variant="outline"
            onClick={handleReset}
            data-testid="bday-sms-reset">
            Réinitialiser le modèle par défaut
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default BirthdaySmsTab;
